import { Link } from "react-router-dom";
import { Monitor, MessageSquareText, ClipboardCheck, BookOpenCheck, School, Landmark, Globe2, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { SectionHeading } from "@/components/shared";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { DotGrid } from "@/components/visuals";

const LMS_FEATURES = [
  { icon: BookOpenCheck, title: "Lessons & materials", text: "Every unit, example and practice set lives in one portal — open 24/7 from any device." },
  { icon: MessageSquareText, title: "Direct teacher access", text: "Reach your Ontario Certified Teacher right inside the portal for personalized help." },
  { icon: ClipboardCheck, title: "Assessments & quizzes", text: "Assignments, unit tests and quizzes, all completed and graded through the LMS." },
  { icon: Monitor, title: "Proctored final exam", text: "Book and write your supervised final exam through the same platform — from home." },
];

const PROCESS_STEPS = [
  { title: "Choose your courses", text: "Decide on the courses you want to study within our comprehensive curriculum.", img: "https://images.pexels.com/photos/4260484/pexels-photo-4260484.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "A student browses course options on her laptop at a bright desk" },
  { title: "Register online", text: "Fill in your details and securely upload the necessary documents.", img: "https://images.pexels.com/photos/5905886/pexels-photo-5905886.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "A student types her registration details on a laptop" },
  { title: "Records & PLAR", text: "International and out-of-province students: await your PLAR outcome and the setup of your Ontario Student Record (OSR). Ontario students keeping their day school as their full-time school skip to the next step.", img: "https://images.pexels.com/photos/6538437/pexels-photo-6538437.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "Admissions staff review transcripts and documents at a desk" },
  { title: "Admission confirmed", text: "Receive your formal admission confirmation on your registered email.", img: "https://images.pexels.com/photos/3769021/pexels-photo-3769021.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "A student smiles while reading her admission confirmation on a laptop" },
  { title: "Log in & start", text: "Sign in to the Learning Portal with your credentials — and start your studies at your own pace immediately.", img: "https://images.pexels.com/photos/36764624/pexels-photo-36764624.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "A student logs into his learning portal at home" },
  { title: "Connect with your teachers", text: "Reach out to your teachers directly from the portal for any personalized help.", img: "https://images.pexels.com/photos/5905741/pexels-photo-5905741.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "A video call between a student and her teacher" },
  { title: "Learn & assess", text: "Pass the assessments and quizzes aimed at mastering the course objectives.", img: "https://images.pexels.com/photos/6256016/pexels-photo-6256016.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "A student works through coursework on a laptop at home" },
  { title: "Proctored final exam", text: "Take your proctored final exam — the milestone towards course completion.", img: "https://images.pexels.com/photos/7092515/pexels-photo-7092515.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "A focused student takes an exam at a desk with a laptop" },
  { title: "Earn Credits and High School Diploma", text: "Receive your OSSD and Ontario Student Transcript (OST). For Ontario-based students who continue with their day school, we grant your credits and send those to your full-time school for your OSSD.", img: "https://images.pexels.com/photos/27300388/pexels-photo-27300388.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "Graduates celebrate on the steps of a university building" },
  { title: "Apply to university", text: "Apply to universities — we handle submission of your marks directly to OUAC/OCAS.", img: "https://images.pexels.com/photos/7972324/pexels-photo-7972324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "Students walk together across a university campus" },
];

const PATHS = [
  { icon: School, title: "Ontario students", text: "Keeping your day school? Stay registered there as your full-time school — we send your EGOS credits to them and they issue your OSSD. Want EGOS as your home school instead? Transfer your OSR to us and we grant the diploma directly." },
  { icon: Landmark, title: "Out-of-province students", text: "Your OSR is set up with EGOS, and our principal reviews your previous transcripts — matching credits can be awarded through PLAR equivalency, so you never retake what you have already finished." },
  { icon: Globe2, title: "International students", text: "No study permit is needed to start. Your OSR is set up with EGOS, prior international coursework can count through PLAR equivalency, and your final marks go straight to OUAC — your OSSD is issued by us." },
];

const PROCESS_FAQS = [
  { q: "How long does the admission process take?", a: "Once your documents are uploaded, the initial review typically takes 2–3 business days. International and out-of-province students awaiting PLAR outcomes and the setup of an Ontario Student Record (OSR) should allow slightly longer. Ontario students transferring their OSR from another school should also account for the time this takes, including steps completed by their previous school." },
  { q: "What is the PLAR process?", a: "Students registering from outside Ontario can transfer previous education toward the OSSD through Prior Learning Assessment and Recognition (PLAR). Under the equivalency pathway, the EGOS principal reviews your international transcripts and grants OSSD credits based on how closely your past studies match Ontario's curriculum — saving you from retaking courses you have already completed." },
  { q: "What support is available during my studies?", a: "You have direct access to Ontario Certified Teachers through the Learning Portal. Reach out anytime for help with course content, technical issues or academic guidance — it all happens in the portal itself." },
  { q: "Can I start my courses immediately after admission?", a: "Yes. As soon as your admission confirmation and portal credentials arrive, you can log in and begin studying at your own pace from anywhere in the world." },
  { q: "Will EGOS issue an OSSD to every student?", a: "If you are an Ontario student, your OSR needs to be transferred to us from your previous school for EGOS to become your full-time school and grant the OSSD. If you are taking only specific credits with us while remaining registered full-time at another school, we send your credits to that school and they issue the OSSD. All international and out-of-province students whose OSR is set up with EGOS receive their OSSD directly from us." },
];

function LmsSection() {
  return (
    <section className="relative overflow-hidden bg-ink py-20 text-ivory sm:py-28" data-testid="process-lms-section">
      <DotGrid light className="right-[6%] top-10 hidden h-40 w-40 lg:block" />
      <div className="container-x">
        <SectionHeading
          dark
          eyebrow="Your Learning Portal"
          title={<>One LMS for your studies <span className="font-serif italic font-medium text-brandlight">and your tests</span></>}
          desc="Every EGOS student gets access to our Learning Management System — the single place where you study, submit, get tested and stay connected to your teachers."
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {LMS_FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.07} className="h-full">
              <div className="h-full rounded-2xl border border-ivory/15 bg-ink2/60 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand/50" data-testid={`process-lms-card-${i}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand text-white">
                  <f.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-display text-base font-bold text-ivory">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ivory/60">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function StepsSection() {
  return (
    <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="process-steps-section">
      <SectionHeading
        eyebrow="The Process"
        title={<>Admissions process, <span className="font-serif italic font-medium text-brand">made simple</span></>}
        desc="Ten steps from choosing a course to a university application — and our team guides you through every milestone in between."
      />
      <div className="mt-16 space-y-16 sm:space-y-24">
        {PROCESS_STEPS.map((s, i) => (
          <Reveal key={s.title}>
            <div className={`flex flex-col items-center gap-8 md:mx-auto md:max-w-5xl md:items-stretch md:gap-14 lg:gap-20 ${i % 2 ? "md:flex-row-reverse" : "md:flex-row"}`} data-testid={`process-step-${i + 1}`}>
              <div className="w-full flex-1">
                <p className="eyebrow">Step {String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-ink">{s.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-slate-600">{s.text}</p>
              </div>
              <div className="relative w-full flex-1">
                <img
                  src={s.img}
                  alt={s.alt}
                  loading="lazy"
                  className="h-64 w-full rounded-3xl border border-slate-200 object-cover shadow-lg shadow-ink/5 sm:h-72"
                  data-testid={`process-step-image-${i + 1}`}
                />
                <span className="absolute -top-4 right-6 flex h-10 w-10 items-center justify-center rounded-full bg-brand font-mono text-sm font-bold text-white shadow-lg shadow-brand/30" data-testid={`process-step-badge-${i + 1}`}>
                  {i + 1}
                </span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-16">
        <p className="mx-auto max-w-2xl rounded-3xl border border-brand/40 bg-brandlight/60 p-8 text-center font-display text-lg font-bold text-ink sm:text-xl" data-testid="process-comfort-message">
          And the best part? All ten steps happen <span className="font-serif italic font-medium text-brand">from the comfort of your home.</span>
        </p>
      </Reveal>
    </section>
  );
}

function PathsSection() {
  return (
    <section className="border-y border-slate-200/70 bg-sand/50 py-20 sm:py-28 lg:py-32" data-testid="process-paths-section">
      <div className="container-x">
        <SectionHeading
          eyebrow="Your Starting Point"
          title={<>Three paths, <span className="font-serif italic font-medium text-brand">one diploma</span></>}
          desc="Where you are coming from decides how your records are set up — the destination is the same."
        />
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {PATHS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08} className="h-full">
              <div className="card-line h-full p-8" data-testid={`process-path-${i}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ink text-brandlight">
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-display text-lg font-bold text-ink">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="process-faq-section">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Process Questions"
            title={<>Timelines, records, <span className="font-serif italic font-medium text-brand">answered</span></>}
            desc="The five questions families ask most about starting with EGOS."
          />
        </div>
        <div className="lg:col-span-7">
          <Reveal>
            <Accordion type="single" collapsible className="card-line bg-white px-6" data-testid="process-faq-accordion">
              {PROCESS_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`process-faq-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base font-semibold text-ink" data-testid={`process-faq-item-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="container-x pb-24" data-testid="process-cta-section">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl bg-brand px-8 py-14 text-center sm:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-white/80">Ready when you are</p>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
            Start your application today.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/85 sm:text-base">
            Join our global community and take the first step toward your OSSD. Our admissions team is here to guide you through every stage of the process.
          </p>
          <div className="mt-8">
            <Link to="/admissions" data-testid="process-cta-apply" className="btn-primary !bg-ink hover:!bg-ink2">
              Apply in <span className="text-brand">5 minutes</span> <ArrowRight className="h-4 w-4 text-brand" />
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export default function Process() {
  return (
    <>
      <PageHero
        eyebrow="Admission Process"
        lines={[<>Admissions process</>, <span className="font-serif italic font-medium text-brand">made simple</span>]}
        desc="Your journey to an Ontario Secondary School Diploma starts here. Our team guides you through every milestone of your academic journey."
        testid="process-page-heading"
      />

      <LmsSection />

      <StepsSection />

      <PathsSection />

      <FaqSection />

      <CtaSection />
    </>
  );
}
